import { Box } from "ui/box";
import { Select } from "ui/select";
import { Dialog } from "ui/dialog";
import VersionCompareColumn from "components/modals/compare-versions-modal/components/version-compare-column";

import { useCompareVersions } from "components/modals/compare-versions-modal/hooks/use-compare-versions";

import {
  COMPARISON_SX,
  PICKERS_ROW_SX,
  PICKER_CELL_SX,
  VERSION_SELECT_SX,
  PREVIEWS_SCROLL_SX,
  PREVIEWS_ROW_SX,
} from "components/modals/compare-versions-modal/compare-versions-modal.styles";

interface CompareVersionsModalProps {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  touchpointVersionId: string;
  hideSubjectAndPreheader?: boolean;
}

const CompareVersionsModal = ({
  isOpen,
  setIsOpen = () => {},
  touchpointVersionId,
  hideSubjectAndPreheader = false,
}: CompareVersionsModalProps) => {
  const {
    touchpointData,
    isLoadingLeft,
    isLoadingRight,
    selectedLeftVersionId,
    selectedRightVersionId,
    leftVersionSelectOptions,
    rightVersionSelectOptions,
    isEmailType,
    handleLeftVersionChange,
    handleRightVersionChange,
  } = useCompareVersions({
    touchpointVersionId,
    isOpen,
  });

  if (!isOpen) {
    return null;
  }

  const hasLeftVersion =
    !isLoadingLeft && !!touchpointData.leftVersion?.html && !!touchpointData.leftVersion?.beeJson;
  const hasRightVersion =
    !isLoadingRight &&
    !!touchpointData.rightVersion?.html &&
    !!touchpointData.rightVersion?.beeJson;
  const showLeftError = !isLoadingLeft && !hasLeftVersion;
  const showRightError = !isLoadingRight && !hasRightVersion;

  return (
    <Dialog
      open
      onClose={() => setIsOpen(false)}
      size="large"
      width={900}
      headline="Compare creative versions">
      <Box sx={COMPARISON_SX}>
        <Box sx={PICKERS_ROW_SX}>
          <Box sx={PICKER_CELL_SX}>
            <Select
              size="small"
              selectName="left-version-select"
              disableClearable
              onChange={(newValue) => newValue && handleLeftVersionChange(newValue)}
              options={leftVersionSelectOptions}
              value={leftVersionSelectOptions.find(
                (option) => option.value === selectedLeftVersionId,
              )}
              ariaLabel="Select left version to compare"
              sx={VERSION_SELECT_SX}
            />
          </Box>
          <Box sx={PICKER_CELL_SX}>
            <Select
              size="small"
              selectName="right-version-select"
              disableClearable
              onChange={(newValue) => newValue && handleRightVersionChange(newValue)}
              options={rightVersionSelectOptions}
              value={rightVersionSelectOptions.find(
                (option) => option.value === selectedRightVersionId,
              )}
              ariaLabel="Select right version to compare"
              sx={VERSION_SELECT_SX}
            />
          </Box>
        </Box>
        <Box sx={PREVIEWS_SCROLL_SX}>
          <Box sx={PREVIEWS_ROW_SX}>
            <VersionCompareColumn
              isLoading={isLoadingLeft}
              hasVersion={hasLeftVersion}
              showError={showLeftError}
              version={touchpointData.leftVersion}
              isEmailType={isEmailType}
              hideSubjectAndPreheader={hideSubjectAndPreheader}
            />
            <VersionCompareColumn
              isLoading={isLoadingRight}
              hasVersion={hasRightVersion}
              showError={showRightError}
              version={touchpointData.rightVersion}
              isEmailType={isEmailType}
              hideSubjectAndPreheader={hideSubjectAndPreheader}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CompareVersionsModal;
